import { InfoCircleOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import React, { FC } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'

const TooltipContent = styled.div`
  max-width: 450px;
  max-height: 400px;
  overflow-y: auto;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.5;
  color: #ffffff;
  background-color: transparent;
  padding: 0 16px;

  h1 {
    font-size: 18px !important;
    font-weight: 700 !important;
    margin-top: 0 !important;
    margin-bottom: 12px !important;
    color: #ffffff !important;
    border-bottom: 2px solid #595959 !important;
    padding-bottom: 4px !important;
    display: block !important;
  }

  h2 {
    font-size: 16px !important;
    font-weight: 700 !important;
    margin-top: 16px !important;
    margin-bottom: 8px !important;
    color: #f0f0f0 !important;
    display: block !important;
  }

  h3 {
    font-size: 14px !important;
    font-weight: 600 !important;
    margin-top: 12px !important;
    margin-bottom: 6px !important;
    color: #e0e0e0 !important;
    display: block !important;
  }

  h4,
  h5,
  h6 {
    font-size: 13px !important;
    font-weight: 600 !important;
    margin-top: 8px !important;
    margin-bottom: 4px !important;
    color: #e0e0e0 !important;
    display: block !important;
  }

  p {
    margin-bottom: 8px;
    color: #ffffff !important;
  }

  ul,
  ol {
    margin-bottom: 8px;
    padding-left: 16px;
    color: #ffffff !important;
  }

  li {
    margin-bottom: 4px;
    color: #ffffff !important;
  }

  code {
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Consolas', monospace;
  }

  pre {
    background-color: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
  }

  hr {
    border: none;
    border-top: 1px solid #595959;
    margin: 16px 0;
  }

  strong {
    font-weight: 700 !important;
    color: #ffffff !important;
  }

  em {
    font-style: italic !important;
    color: #e0e0e0 !important;
  }
`

const StyledInfoIcon = styled(InfoCircleOutlined)<{ $spaceLeft?: boolean }>`
  display: inline-flex;
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8c8c8c;
  font-size: 14px;
  line-height: 1;
  margin-left: ${({ $spaceLeft }) => ($spaceLeft ? '6px' : '0')};

  &:hover {
    color: ${({ theme }) => theme.forecast?.primary || '#007BFF'};
  }
`

const LabelWithTooltipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

export interface TooltipConfig {
  format: 'markdown' | 'html' | 'text'
  content: string
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
  trigger?: 'hover' | 'click' | 'focus'
}

interface DynamicFieldTooltipProps {
  tooltip: TooltipConfig
  spaceLeft?: boolean
}

/**
 * Dynamic tooltip component that renders based on tooltip configuration.
 * Automatically handles different content formats and positioning.
 */
export const DynamicFieldTooltip: FC<DynamicFieldTooltipProps> = ({
  tooltip,
  spaceLeft = true,
}) => {
  const renderContent = () => {
    switch (tooltip.format) {
      case 'markdown':
        return (
          <TooltipContent>
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1>{children}</h1>,
                h2: ({ children }) => <h2>{children}</h2>,
                h3: ({ children }) => <h3>{children}</h3>,
                h4: ({ children }) => <h4>{children}</h4>,
                h5: ({ children }) => <h5>{children}</h5>,
                h6: ({ children }) => <h6>{children}</h6>,
              }}>
              {tooltip.content}
            </ReactMarkdown>
          </TooltipContent>
        )
      case 'html':
        return (
          <TooltipContent
            dangerouslySetInnerHTML={{ __html: tooltip.content }}
          />
        )
      case 'text':
      default:
        return (
          <TooltipContent>
            <div style={{ whiteSpace: 'pre-wrap' }}>{tooltip.content}</div>
          </TooltipContent>
        )
    }
  }

  return (
    <Popover
      content={renderContent()}
      trigger={tooltip.trigger || 'hover'}
      placement={tooltip.placement || 'top'}>
      <StyledInfoIcon $spaceLeft={spaceLeft} />
    </Popover>
  )
}

interface LabelWithDynamicTooltipProps {
  label: string
  tooltip: TooltipConfig
}

/**
 * Convenience component that renders a label with a dynamic tooltip.
 */
export const LabelWithDynamicTooltip: FC<LabelWithDynamicTooltipProps> = ({
  label,
  tooltip,
}) => {
  return (
    <LabelWithTooltipContainer>
      {label}
      <DynamicFieldTooltip tooltip={tooltip} />
    </LabelWithTooltipContainer>
  )
}
